import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Medal, ArrowLeft, Share2, FastForward } from 'lucide-react';
import { useGame, useFinalizeMultiGame, useGameSubmissions, useStartNextGame } from './api';
import { useRoom, useTournament } from '@/features/rooms/api';
import { useCheckMyAchievements } from '@/features/achievements/api';
import { useCheckMyCosmetics } from '@/features/cosmetics/api';
import { useCheckMyMissions } from '@/features/battle-pass/api';
import { ShareScoreModal } from '@/features/sharing/ShareScoreModal';
import { useAuth } from '@/providers/AuthProvider';
import { celebrateCascade, celebrateLegendary } from '@/lib/celebrations';
import { Card, CardDescription, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Avatar } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Loading';
import { useToast } from '@/components/ui/Toast';
import { cn, formatCompact } from '@/lib/utils';
/**
 * Affichage du podium d'une partie multi. Si le game n'est pas encore
 * finalisé, l'hôte voit un bouton pour appeler `finalize_multi_game`.
 */
export function MultiPodiumScreen({ game, currentPlayerId, onBackToLobby, }) {
    const { user } = useAuth();
    const { data: room } = useRoom(game.room_id ?? undefined);
    const { data: tournament } = useTournament(game.room_id ?? undefined);
    const { data: liveGame } = useGame(game.id);
    // Polling court tant que la manche n'est pas finalisée : on n'a pas de
    // canal Realtime sur game_actions filtré par game_id, donc on rafraîchit
    // toutes les 2s pour que le compteur "X joueurs ont terminé" soit à jour
    // côté host. S'arrête dès que game.status='finished'.
    const { data: submissions = [] } = useGameSubmissions(game.id, {
        refetchInterval: liveGame?.status === 'finished' ? false : 2_000,
    });
    const finalize = useFinalizeMultiGame();
    const startNext = useStartNextGame();
    const checkAch = useCheckMyAchievements();
    const checkCos = useCheckMyCosmetics();
    const checkMis = useCheckMyMissions();
    const { toast } = useToast();
    const navigate = useNavigate();
    const planned = (tournament?.games_planned ?? []);
    const hasNextRound = planned.length > 0 && (tournament?.current_game_index ?? 0) < planned.length;
    const isHost = user?.id === room?.host_id;
    const results = liveGame?.results;
    const [shareOpen, setShareOpen] = useState(false);
    const myEntry = results?.find((e) => e.player_id === currentPlayerId);
    // Check achievements une fois quand le jeu passe en 'finished' (chaque
    // joueur voit ses propres déblocages, scope auth.uid()).
    const checkedRef = useRef(false);
    useEffect(() => {
        if (liveGame?.status !== 'finished' || checkedRef.current)
            return;
        checkedRef.current = true;
        // Cascade dorée à la révélation du podium (cérémonie).
        celebrateCascade();
        void (async () => {
            try {
                const newAch = await checkAch.mutateAsync();
                for (const a of newAch) {
                    toast({
                        tone: 'achievement',
                        title: 'Achievement débloqué !',
                        description: `${a.name} · +${a.xp_reward} XP (${a.rarity})`,
                    });
                    if (a.rarity === 'legendary')
                        celebrateLegendary();
                }
            }
            catch {
                /* best-effort */
            }
            try {
                const newCos = await checkCos.mutateAsync();
                for (const c of newCos) {
                    toast({
                        tone: 'achievement',
                        title: 'Nouveau cosmétique !',
                        description: `${c.name} (${c.type})`,
                    });
                }
            }
            catch {
                /* best-effort */
            }
            try {
                const newMis = await checkMis.mutateAsync();
                for (const m of newMis) {
                    toast({
                        tone: 'success',
                        title: 'Mission complétée',
                        description: `${m.name} · +${m.bpxp_reward} BPXP à réclamer`,
                    });
                }
            }
            catch {
                /* best-effort */
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [liveGame?.status]);
    // Pas encore finalisé : hôte clique pour calculer.
    if (liveGame?.status !== 'finished') {
        return (_jsxs(Card, { variant: "glow", padding: "lg", className: "mx-auto max-w-md text-center", children: [_jsx(Trophy, { className: "mx-auto mb-3 h-12 w-12 text-gold animate-bounce-soft" }), _jsx(CardTitle, { children: "Manche en cours" }), _jsxs(CardDescription, { className: "mt-2", children: [submissions.length, " joueur(s) ont termin\u00E9. ", isHost
                            ? 'Clôture la manche pour révéler le podium.'
                            : "L'hôte va clôturer la manche."] }), isHost && (_jsx("div", { className: "mt-4 flex justify-center", children: _jsx(Button, { variant: "primary", leftIcon: _jsx(Trophy, { className: "h-4 w-4" }), isLoading: finalize.isPending, onClick: async () => {
                            try {
                                await finalize.mutateAsync(game.id);
                            }
                            catch (err) {
                                toast({ tone: 'error', title: 'Échec', description: err.message });
                            }
                        }, children: "R\u00E9v\u00E9ler le podium" }) }))] }));
    }
    const entries = Array.isArray(results) ? results : [];
    const podium = entries.slice(0, 3);
    const others = entries.slice(3);
    return (_jsxs("div", { className: "space-y-6", children: [_jsxs("header", { className: "text-center", children: [_jsx(Trophy, { className: "mx-auto h-12 w-12 text-gold drop-shadow-[0_4px_16px_rgba(255,215,0,.6)] animate-bounce-soft" }), _jsx("h2", { className: "mt-2 font-display text-3xl uppercase tracking-tight text-gradient-flame", children: "Podium" })] }), _jsxs("div", { className: "grid grid-cols-3 items-end gap-3", children: [_jsx(PodiumStep, { entry: podium[1], place: 2, currentPlayerId: currentPlayerId }), _jsx(PodiumStep, { entry: podium[0], place: 1, currentPlayerId: currentPlayerId }), _jsx(PodiumStep, { entry: podium[2], place: 3, currentPlayerId: currentPlayerId })] }), others.length > 0 && (_jsxs(Card, { padding: "md", children: [_jsx(CardTitle, { className: "mb-2 text-base", children: "Autres joueurs" }), _jsx("ul", { className: "space-y-2", children: others.map((e) => (_jsxs("li", { className: cn('flex items-center gap-3 rounded-2xl bg-ink-900/40 p-3', e.player_id === currentPlayerId && 'ring-1 ring-electric-500/40'), children: [_jsx("span", { className: "w-8 text-center font-display text-lg text-ink-400", children: e.position }), _jsx(Avatar, { pseudo: e.pseudo, src: e.avatar_url, size: "md" }), _jsx("div", { className: "flex-1", children: _jsx("p", { className: "font-display text-sm font-semibold text-ink-50", children: e.pseudo }) }), _jsx("p", { className: "font-mono text-sm text-ink-300", children: formatCompact(e.score) })] }, e.player_id))) })] })), _jsxs("div", { className: "flex flex-wrap justify-end gap-2", children: [_jsx(Button, { variant: "ghost", leftIcon: _jsx(ArrowLeft, { className: "h-4 w-4" }), onClick: () => {
                            if (room?.code)
                                navigate(`/lobby/${room.code}`);
                            else
                                onBackToLobby();
                        }, children: "Retour au lobby" }), myEntry && (_jsx(Button, { variant: "flame", leftIcon: _jsx(Share2, { className: "h-4 w-4" }), onClick: () => setShareOpen(true), children: "Partager mon score" })), isHost && hasNextRound && room && (_jsxs(Button, { variant: "primary", leftIcon: _jsx(FastForward, { className: "h-4 w-4" }), isLoading: startNext.isPending, onClick: async () => {
                            try {
                                const nextId = await startNext.mutateAsync({ roomId: room.id });
                                navigate(`/game/${nextId}`);
                            }
                            catch (err) {
                                toast({ tone: 'error', title: 'Échec', description: err.message });
                            }
                        }, children: ["Manche suivante (", (tournament?.current_game_index ?? 0) + 1, "/", planned.length, ")"] }))] }), myEntry && (_jsx(ShareScoreModal, { open: shareOpen, onOpenChange: setShareOpen, gameType: game.type, score: myEntry.score, meta: myEntry.meta ?? undefined }))] }));
}
function PodiumStep({ entry, place, currentPlayerId, }) {
    const heights = {
        1: 'h-44 md:h-56',
        2: 'h-32 md:h-40',
        3: 'h-24 md:h-32',
    };
    const colors = {
        1: 'from-gold/40 to-gold/10 border-gold/50 shadow-glow-gold',
        2: 'from-silver/40 to-silver/10 border-silver/50',
        3: 'from-bronze/40 to-bronze/10 border-bronze/50',
    };
    const medalIcons = {
        1: _jsx(Trophy, { className: "h-6 w-6 text-gold" }),
        2: _jsx(Medal, { className: "h-6 w-6 text-silver" }),
        3: _jsx(Medal, { className: "h-6 w-6 text-bronze" }),
    };
    if (!entry) {
        return (_jsx("div", { className: cn('rounded-2xl border border-dashed border-ink-700 bg-ink-900/30', heights[place]) }));
    }
    const isMe = entry.player_id === currentPlayerId;
    return (_jsxs("div", { className: "flex flex-col items-center gap-2", children: [_jsxs("div", { className: "flex flex-col items-center gap-1", children: [_jsx(Avatar, { pseudo: entry.pseudo, src: entry.avatar_url, size: place === 1 ? '2xl' : 'xl', border: place === 1 ? 'gold' : place === 2 ? 'silver' : 'bronze' }), _jsx("p", { className: "font-display text-sm font-semibold text-ink-50", children: entry.pseudo }), isMe && _jsx(Badge, { size: "sm", variant: "electric", children: "Toi" })] }), _jsxs("div", { className: cn('flex w-full flex-col items-center justify-end gap-1 rounded-2xl border bg-gradient-to-t pb-3 pt-6', heights[place], colors[place]), children: [medalIcons[place], _jsxs("p", { className: "font-display text-3xl text-ink-50", children: ["#", place] }), _jsxs("p", { className: "font-mono text-sm text-ink-200", children: [formatCompact(entry.score), " pts"] })] })] }));
}
// Re-export pour faciliter l'import depuis Game.tsx
export { Spinner };
